/// <reference path="../types/three-jsx-elements.d.ts" />
import { useRef, useState, FC, ReactNode, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import useSound from '../hooks/useSound';
import styles from './FloatingPanel.module.css';
import { Group } from '../utils/ThreeElements';
import Panel2D from './Panel2D';

export interface FloatingPanelProps {
  title?: string;
  position?: [number, number, number];
  width: number;
  height: number;
  children?: ReactNode;
  isMobile?: boolean;
  onClick?: () => void;
  active?: boolean;
  className?: string;
}

const FloatingPanel: FC<FloatingPanelProps> = ({ 
  title, 
  position, 
  width, 
  height, 
  children,
  isMobile = false,
  onClick,
  active = false,
  className = ""
}) => {
  const panelRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);
  const hoverRef = useRef(false);
  const { playHover, playClick } = useSound();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Handle responsiveness
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Check if we should render 2D fallback for mobile
  const shouldRender2D = windowWidth <= 768 || isMobile;
  
  useFrame(({ clock }) => {
    if (!panelRef.current || shouldRender2D) return;
    
    // Enhanced floating animation
    const t = clock.getElapsedTime();
    const floatIntensity = hoverRef.current ? 0.05 : 0.1;
    const rotationIntensity = hoverRef.current ? 0.001 : 0.01;
    
    // Fix: Add null check for position and provide default value
    const pos = position || [0, 0, 0];
    panelRef.current.position.y = pos[1] + Math.sin(t * 0.5) * floatIntensity;
    panelRef.current.rotation.x = Math.sin(t * 0.5) * rotationIntensity;
    panelRef.current.rotation.y = Math.cos(t * 0.3) * rotationIntensity;
  });
  
  const handlePointerOver = () => {
    hoverRef.current = true;
    setHovered(true);
    playHover();
  };
  
  const handlePointerOut = () => {
    hoverRef.current = false;
    setHovered(false);
  };
  
  const handleClick = () => {
    if (onClick) {
      playClick();
      onClick();
    }
  };
  
  // 2D fallback for mobile devices - use the Panel2D component
  if (shouldRender2D) {
    return (
      <Panel2D
        title={title}
        width={width * 70}
        height={height * 70}
        className={className}
        onClick={onClick}
        active={active}
      >
        {children}
      </Panel2D>
    );
  }
  
  return (
    <Group 
      ref={panelRef} 
      position={position}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onClick={handleClick}
    >
      {/* Remove all box-creating elements for the About section panel */}
      
      {/* Content - Now directly rendered without any surrounding box */}
      {children && (
        <Html position={[0, 0, 0.01]} center transform>
          <div 
            className={`${styles.panelContent} ${active ? styles.activeContent : ''} ${className}`}
            style={{ 
              width: `${width * 70}px`,
              maxHeight: `${height * 70}px`,
              background: 'transparent'
            }}
          >
            {children}
          </div>
        </Html>
      )}
    </Group>
  );
};

export { FloatingPanel };
export default FloatingPanel;